import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Spacing, Colors, Dimensions } from '@/shared/theme';
import { Text, Button } from '@/shared/ui';
import { ResultTitle, ResultBody } from '@/shared/resultText';
import { TextField } from '@mui/material';
import { StartOverModal } from '@/components/StartOverModal';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import { VIEW_CAP_REACHED, GET_NOTIFIED } from '@/consts/events';
import { useRegistration } from '@/context/RegistrationContext';
import { useApiError } from '@/context/ApiErrorContext';
import { apiClient } from '@/services/api';
// TODO: Replace with actual cap-reached illustration
import capReachedImage from '@/assets/images/telescope.svg';

// A POST_REGISTRATION_PATHS route (Layout.tsx): a plain, unframed, normal
// page-scroll screen with no rail/phase-bar to anchor to. min-height +
// justify-content:center keep this vertically centered (Boris `.rg-result`)
// instead of clumping at the top of an otherwise-empty cream page, matching
// EligibilityResultStep's already-fixed terminal-screen pattern.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: calc(100vh - ${Dimensions.headerHeight}px);
  gap: ${Spacing.x3}px;
  padding: ${Spacing.x4}px ${Spacing.x2}px;
`;

const IllustrationImage = styled.img`
  width: 100%;
  max-width: 200px;
  height: auto;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.x1}px;
  width: 100%;
  max-width: 560px;
  text-align: center;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.x2}px;
  width: 100%;
  max-width: 560px;
  margin-top: ${Spacing.x1}px;
`;

const FormTitle = styled(Text)`
  font-weight: 700;
`;

const StyledTextField = styled(TextField)`
  width: 100%;

  & .MuiOutlinedInput-root {
    border-radius: 8px;
  }

  & .MuiInputLabel-root {
    color: ${Colors.foreground};
  }
`;

const CapReachedStep: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackView, track } = useAnalytics();
  const { registrationData, webregUserId } = useRegistration();
  const { showApiError } = useApiError();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [showStartOverModal, setShowStartOverModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Integrate useAppFlow with back navigation blocking
  const { resetBlocker } = useAppFlow(STEP_KEYS.PROMO_CODE_CAP_REACHED, {
    onBackNavigationBlocked: () => {
      setShowStartOverModal(true);
    },
  });

  useEffect(() => {
    trackView({ event: VIEW_CAP_REACHED });
  }, [trackView]);

  const validateEmail = (value: string): string => {
    if (!value.trim()) {
      return t('cap-reached.email-required');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return t('cap-reached.email-invalid');
    }
    return '';
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    if (emailTouched) {
      setEmailError(validateEmail(value));
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailError(validateEmail(email));
  };

  const handleNotifyMe = async () => {
    // Validate form
    track({ event: GET_NOTIFIED });
    const error = validateEmail(email);
    setEmailError(error);
    setEmailTouched(true);

    if (error) {
      return;
    }

    // Get promo code from registration data
    const promoCode = registrationData.promoCodeData!.code;

    setIsSubmitting(true);

    try {
      // Call API to record failed registration attempt
      await apiClient.recordFailedRegistration({
        webregUserId,
        identifier: email,
        reason: 'CAP_REACHED',
        promoCode,
      });

      // Navigate to email-sent confirmation page
      navigate('/email-sent', { state: { email } });
    } catch (error) {
      console.error('Failed to record failed registration:', error);
      showApiError();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartOver = () => {
    setShowStartOverModal(false);
    navigate('/');
  };

  const isFormValid = email.trim() && !emailError;

  return (
    <Container>
      <IllustrationImage src={capReachedImage} alt="" aria-hidden="true" />
      <ContentWrapper>
        <ResultTitle size="h3" centered>
          {t('cap-reached.title')}
        </ResultTitle>

        <ResultBody size="p1" centered>
          {t('cap-reached.message')}
        </ResultBody>
      </ContentWrapper>

      <FormSection>
        <FormTitle size="h6" centered>
          {t('cap-reached.form-title')}
        </FormTitle>

        <StyledTextField
          label={t('cap-reached.email-label')}
          type="email"
          value={email}
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
          error={emailTouched && !!emailError}
          helperText={emailTouched && emailError}
          variant="outlined"
          fullWidth
          inputProps={{
            'aria-label': t('cap-reached.email-label'),
          }}
        />

        <Button
          text={t('cap-reached.notify-button')}
          onClick={handleNotifyMe}
          variant={isSubmitting ? 'loading' : isFormValid ? 'brand-primary' : 'disabled'}
        />
      </FormSection>

      {/* Start Over Modal */}
      <StartOverModal
        isOpen={showStartOverModal}
        onConfirm={handleStartOver}
        onCancel={() => {
          setShowStartOverModal(false);
          resetBlocker();
        }}
      />
    </Container>
  );
};

export { CapReachedStep };
